import ChalkySticks from '@chalkysticks/sdk';
import Router from '@/Router';
import { ActionContext } from 'vuex';
import { IStoreState } from './State';

/**
 * @type ActionContext<S, R>
 */
type Context = ActionContext<IStoreState, IStoreState>;

/**
 * Actions are generally meant to be the entrypoint for modifying state.
 *
 * They are asynchronous calls that can perform other tasks and then
 * trigger a mutation (synchronous).
 *
 * @see https://vuex.vuejs.org/guide/actions.html
 *
 * @class Actions
 * @package Store
 * @project ChalkySticks App
 */
const actions = {
	/**
	 * Navigate to a user's profile page and store the user model
	 * in state for the target page to consume.
	 *
	 * @param Context context
	 * @param ChalkySticks.Model.User userModel
	 * @return void
	 */
	gotoUser(context: Context, userModel: ChalkySticks.Model.User): void {
		userModel.cancelModifiedEndpoint();

		context.state.navigatingUserModel = userModel;

		Router.push({
			name: 'PageUser',
			params: {
				id: userModel.getSlug(),
			},
		});
	},

	/**
	 * Navigate to a venue's detail page and store the venue model
	 * in state for the target page to consume.
	 *
	 * @param Context context
	 * @param ChalkySticks.Model.Venue venueModel
	 * @return void
	 */
	gotoVenue(context: Context, venueModel: ChalkySticks.Model.Venue): void {
		venueModel.cancelModifiedEndpoint();

		context.state.navigatingVenueModel = venueModel;

		Router.push({
			name: 'PagePlace',
			params: {
				id: venueModel.getSlug(),
			},
		});
	},

	/**
	 * Set the visibility of the primary context menu
	 *
	 * @param Context context
	 * @param boolean payload
	 * @return void
	 */
	setShowPrimaryContextMenu(context: Context, payload: boolean): void {
		context.commit('setShowPrimaryContextMenu', payload);
	},

	/**
	 * Toggle the visibility of the primary context menu
	 *
	 * @param Context context
	 * @return void
	 */
	togglePrimaryContextMenu(context: Context): void {
		context.commit('setShowPrimaryContextMenu', !context.state.showPrimaryContextMenu);
	},
};

export default actions;
